import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { DialogService } from 'primeng/dynamicdialog';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { AssetsService } from '../../../../../../services/api/generated/assets/assets.service';
import { CampaignsService } from '../../../../../../services/api/generated/campaigns/campaigns.service';
import {
  CampaignImportConflictsResponse,
  CampaignImportJsonSchema,
  CampaignImportResolveInput,
  CampaignImportResolveResponse,
  CampaignImportResponse,
  CampaignModelCreateInput,
  CampaignModelCreateResponse,
  MergeConflictRow,
} from '../../../../../../services/api/generated/model';
import { CampaignModelDialogService } from '../../../../../../services/campaign-model-dialog.service';
import { ConfirmationHelperService } from '../../../../../../services/confirmation-helper.service';
import { ModalHelperService } from '../../../../../../services/modal-helper.service';
import { ToastService } from '../../../../../../services/toast.service';
import { CampaignMeasureStore } from '../../../../../../stores/campaign-measure.store';
import { CampaignModelStore } from '../../../../../../stores/campaign-model.store';
import { CampaignStore } from '../../../../../../stores/campaign.store';
import { Campaign, CampaignStatus } from '../../../../../../types/campaign.interface';
import {
  ConflictResolution,
  MergeAnalysis,
  MergeConflict,
  toMergeResolutionEntry,
} from '../../../../../../types/campaign-merge.interface';
import { LogDetailChangeType } from '../../../../../../types/log.interface';
import { downloadBlob } from '../../../../../../utils/download.utils';
import { buildCampaignExportFilename } from '../../../../../../utils/export-filename.utils';
import {
  CampaignConflictDialogComponent,
  type ConflictDialogData,
  ConflictDialogResult,
} from '../dialogs/campaign-conflict-dialog.component';
import { CampaignHistoryService } from './campaign-history.service';

@Injectable({
  providedIn: 'root',
})
export class CampaignActionsService {
  private readonly campaigns = inject(CampaignsService);
  private readonly assets = inject(AssetsService);
  private readonly campaignModelDialogService = inject(CampaignModelDialogService);
  private readonly campaignStore = inject(CampaignStore);
  private readonly campaignMeasureStore = inject(CampaignMeasureStore);
  private readonly campaignModelStore = inject(CampaignModelStore);
  private readonly historyService = inject(CampaignHistoryService);
  private readonly confirmationHelper = inject(ConfirmationHelperService);
  private readonly dialogService = inject(DialogService);
  private readonly modalHelper = inject(ModalHelperService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslocoService);
  private readonly router = inject(Router);

  async updateStatusWithHistory(campaign: Campaign, status: CampaignStatus): Promise<void> {
    try {
      await this.historyService.updateCampaignStatusWithHistory(campaign.id, status);
      this.toastService.success('common.toasts.updateSuccess');
    } catch {
      this.toastService.error('common.toasts.operationFailed');
    }
  }

  async deleteWithHistory(campaign: Campaign): Promise<void> {
    const entityLabel = this.translate.translate('entities.campaign.singular');
    const campaignDisplayName = campaign.timestamp
      ? this.historyService.formatLogDate(campaign.timestamp)
      : `${entityLabel} #${campaign.id}`;
    await this.confirmationHelper.confirmAndDelete({
      entityName: campaignDisplayName,
      messageKey: 'common.messages.confirmDeleteNamed',
      successKey: 'common.toasts.deleteSuccess',
      onDelete: () => this.historyService.deleteCampaignWithHistory(campaign.id),
      onSuccess: () => {
        void this.router.navigate(['/assets', campaign.assetId, 'monitoring', 'campaigns']);
      },
    });
  }

  async exportPDA(campaign: Campaign): Promise<void> {
    try {
      const data = await firstValueFrom(this.campaigns.getCampaignExportJsonResource({ id: campaign.id }));
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const payload = data as { campaign?: { assetName?: string | null; timestamp?: string | null } } | null;
      downloadBlob(blob, buildCampaignExportFilename(payload?.campaign, campaign));

      await this.logAction(campaign.id, 'entities.campaign.messages.pdaExportRequested', 'common.toasts.exportSuccess');
    } catch {
      this.toastService.error('common.toasts.operationFailed');
    }
  }

  async importMeasures(assetId: number): Promise<void> {
    const file = await this.pickJsonFile();
    if (!file) return;

    const payload = await this.parseImportPayload(file);
    if (!payload) return;

    try {
      const result = await firstValueFrom(this.assets.importIntoAsset<CampaignImportResponse>({ id: assetId }, payload));
      const target = this.campaignStore.byId()(result.campaignId);
      if (!target) {
        this.toastService.error('common.toasts.operationFailed');
        return;
      }
      if (result.hasConflicts) {
        await this.openImportConflictDialog(target, result);
        return;
      }
      this.toastService.success('entities.campaign.messages.importSuccess', undefined, undefined, undefined, {
        imported: result.imported,
        updated: result.updated,
        auscultations: result.auscultations,
        outOfBounds: result.outOfBounds ?? 0,
      });
      await this.campaignStore.loadById(result.campaignId, { forceRefresh: true });
      await this.refreshHistory(result.campaignId);
    } catch (err: unknown) {
      this.handleImportError(err);
    }
  }

  // CES-951 §4 : le sélecteur s'ouvre sur le Bureau, là où le Hub dépose les exports du PDA
  // (Desktop/CESmon/exports), puis retient le dossier choisi grâce à l'id, sans réglage utilisateur.
  private static readonly PDA_IMPORT_PICKER: OpenFilePickerOptions = {
    id: 'cesmon-pda-import',
    startIn: 'desktop',
    types: [{ description: 'JSON', accept: { 'application/json': ['.json'] } }],
  };

  private async pickJsonFile(): Promise<File | null> {
    if (!window.showOpenFilePicker) return this.pickJsonFileWithInput();
    try {
      const [handle] = await window.showOpenFilePicker(CampaignActionsService.PDA_IMPORT_PICKER);
      return handle ? await handle.getFile() : null;
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return null;
      // Any other refusal (activation consumed, embedded frame): the classic picker still works.
      return this.pickJsonFileWithInput();
    }
  }

  private pickJsonFileWithInput(): Promise<File | null> {
    return new Promise(resolve => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json';
      input.onchange = () => resolve(input.files?.[0] ?? null);
      input.oncancel = () => resolve(null);
      input.click();
    });
  }

  private async parseImportPayload(file: File): Promise<CampaignImportJsonSchema | null> {
    try {
      const text = await file.text();
      return JSON.parse(text) as CampaignImportJsonSchema;
    } catch {
      this.toastService.error('entities.campaign.messages.invalidJsonFile');
      return null;
    }
  }

  private handleImportError(err: unknown): void {
    const httpError = this.unwrapHttpError(err);
    if (httpError) {
      if (httpError.status === 400) {
        this.toastService.error('entities.campaign.messages.importNoCampaignIdInFile');
        return;
      }
      if (httpError.status === 404) {
        this.toastService.error('entities.campaign.messages.importCampaignNotFoundInAsset');
        return;
      }
    }
    this.toastService.error('common.toasts.operationFailed');
  }

  private unwrapHttpError(err: unknown): HttpErrorResponse | null {
    if (err instanceof HttpErrorResponse) return err;
    if (err instanceof Error && err.cause instanceof HttpErrorResponse) return err.cause;
    return null;
  }

  // The API writes the import entry with every value it applied; the client only re-reads it.
  private refreshHistory(campaignId: number): Promise<void> {
    return this.historyService.logStore.refreshEntityLogs('campaign', campaignId).then(
      () => undefined,
      () => undefined,
    );
  }

  async saveAsModel(campaign: Campaign): Promise<void> {
    try {
      const result = await lastValueFrom(this.campaignModelDialogService.openSaveModelDialog(campaign.id, campaign.assetId));
      if (!result) return;

      const description = (result as { description?: string }).description;
      const body: CampaignModelCreateInput = {
        name: result.name,
        ...(description !== undefined && { description }),
      };
      const createResult = await firstValueFrom(
        this.campaigns.postCampaignSaveAsModel<CampaignModelCreateResponse>({ id: campaign.id }, body),
      );

      if (!createResult) {
        throw new Error(this.translate.translate('entities.campaign.messages.noApiResponse'));
      }

      const modelName = result.name;
      await this.campaignModelStore.getByAssetId(campaign.assetId);

      const campaignDisplayName = campaign.timestamp
        ? this.historyService.formatLogDate(campaign.timestamp)
        : `Campaign #${campaign.id}`;

      const comment = this.translate.translate('entities.campaign.messages.savedAsModel', { name: modelName });
      const entityName = 'entities.campaign.models.singular';
      const parentEntityName = this.translate.translate('history.parentEntity', { name: campaignDisplayName, id: campaign.id });

      await this.historyService.logStore.createLogWithDetails(
        {
          entity: 'campaign',
          entityId: campaign.id,
          comment,
          userId: this.historyService.currentUserId(),
        },
        [
          {
            entity: 'campaign-model',
            entityId: createResult.id,
            attribute: 'name',
            oldValue: null,
            newValue: modelName,
            type: LogDetailChangeType.CREATE,
            entityName,
            parentEntityName,
          },
        ],
      );

      this.toastService.success('entities.campaign.messages.savedAsModel', undefined, undefined, undefined, { name: modelName });
    } catch {
      this.toastService.error('common.toasts.operationFailed');
    }
  }

  async resolveImportConflicts(campaign: Campaign): Promise<void> {
    try {
      const analysis = await firstValueFrom(
        this.campaigns.getCampaignImportConflictsResource<CampaignImportConflictsResponse>({ id: campaign.id }),
      );
      // Pending temp with no remaining conflicts: apply the payload straight away rather than leaving the
      // operator stuck on a flag that yields a silent no-conflicts info toast.
      if (!analysis.hasConflicts) {
        const resolveResponse = await this.resolveImport(campaign.id, []);
        this.toastService.success('entities.campaign.import.resolveSuccess');
        this.campaignStore.updateEntity(campaign.id, resolveResponse.campaign);
        await this.campaignMeasureStore.getByCampaignIdWithDetails(campaign.id, { forceRefresh: true });
        await this.refreshHistory(campaign.id);
        return;
      }
      const ref = this.dialogService.open(
        CampaignConflictDialogComponent,
        this.modalHelper.getMediumConfig({
          data: {
            analysis: this.toMergeAnalysis(analysis.hasConflicts, analysis.conflicts, analysis.campaignAId, analysis.campaignBId),
            campaignA: campaign,
            campaignB: campaign,
            mode: 'import-pda',
          } satisfies ConflictDialogData,
          header: this.translate.translate('entities.campaign.import.conflictsTitle'),
        }),
      );
      const dialogResult = (await lastValueFrom(ref.onClose).catch(() => undefined)) as ConflictDialogResult | undefined;
      if (dialogResult?.abandoned) {
        await firstValueFrom(this.campaigns.deleteCampaignPendingImportResource({ id: campaign.id }));
        this.toastService.success('entities.campaign.import.abandonSuccess');
        await this.campaignStore.loadById(campaign.id, { forceRefresh: true });
      } else if (dialogResult?.resolutions) {
        const resolveResponse = await this.resolveImport(campaign.id, dialogResult.resolutions);
        this.toastService.success('entities.campaign.import.resolveSuccess');
        this.campaignStore.updateEntity(campaign.id, resolveResponse.campaign);
        await this.campaignMeasureStore.getByCampaignIdWithDetails(campaign.id, { forceRefresh: true });
        await this.refreshHistory(campaign.id);
      }
    } catch {
      this.toastService.error('common.toasts.operationFailed');
    }
  }

  private resolveImport(campaignId: number, resolutions: ConflictResolution[]): Promise<CampaignImportResolveResponse> {
    const body: CampaignImportResolveInput = { resolutions: resolutions.map(toMergeResolutionEntry) };
    return firstValueFrom(this.campaigns.postCampaignImportResolveResource<CampaignImportResolveResponse>({ id: campaignId }, body));
  }

  // Wire MergeConflictRow.entityType is a free string while the FE union narrows it; the projection is data-shape compatible.
  private toMergeAnalysis(
    hasConflicts: boolean,
    conflicts: MergeConflictRow[],
    campaignAId: number,
    campaignBId: number,
  ): MergeAnalysis {
    return {
      hasConflicts,
      conflicts: conflicts as unknown as MergeConflict[],
      campaignAId,
      campaignBId,
    };
  }

  private async openImportConflictDialog(campaign: Campaign, importResult: CampaignImportResponse): Promise<void> {
    if (!importResult.tempCampaignId) {
      this.toastService.error('common.toasts.operationFailed');
      return;
    }
    const analysis = this.toMergeAnalysis(true, importResult.conflicts ?? [], campaign.id, importResult.tempCampaignId);
    const ref = this.dialogService.open(
      CampaignConflictDialogComponent,
      this.modalHelper.getMediumConfig({
        data: {
          analysis,
          campaignA: campaign,
          campaignB: campaign,
          mode: 'import-web',
        } satisfies ConflictDialogData,
        header: this.translate.translate('entities.campaign.import.conflictsTitleWeb'),
      }),
    );
    const dialogResult = (await lastValueFrom(ref.onClose).catch(() => undefined)) as ConflictDialogResult | undefined;
    if (dialogResult?.resolutions) {
      try {
        const resolveResponse = await this.resolveImport(campaign.id, dialogResult.resolutions);
        this.toastService.success('entities.campaign.import.resolveSuccess');
        this.campaignStore.updateEntity(campaign.id, resolveResponse.campaign);
        await this.campaignMeasureStore.getByCampaignIdWithDetails(campaign.id, { forceRefresh: true });
        await this.refreshHistory(campaign.id);
      } catch {
        this.toastService.error('common.toasts.operationFailed');
      }
      return;
    }
    try {
      await firstValueFrom(this.campaigns.deleteCampaignPendingImportResource({ id: campaign.id }));
      this.toastService.success('entities.campaign.import.abandonSuccess');
      await this.campaignStore.loadById(campaign.id, { forceRefresh: true });
    } catch {
      this.toastService.error('common.toasts.operationFailed');
    }
  }

  private async logAction(campaignId: number, commentKey: string, successMsg: string): Promise<void> {
    try {
      await this.historyService.logStore.createLogWithDetails(
        {
          entity: 'campaign',
          entityId: campaignId,
          comment: commentKey,
          userId: this.historyService.currentUserId(),
        },
        [],
      );
      this.toastService.info(successMsg);
    } catch {
      this.toastService.error('common.toasts.operationFailed');
    }
  }
}
